// Concrete `DaemonHost` implementations.
//
// Read `./host.ts` first — it explains why this seam exists. This file
// is the only place that knows there are two worlds.
//
//   skenosHost      — DAEMON as an app inside skenOS. The window frame
//                     is the arena, so `setArenaScale` genuinely shrinks
//                     the real WindowFrame via `wm.setBounds`. `say`
//                     goes through the WM's toast system. `unlock` feeds
//                     VAPOUR.
//
//   standaloneHost  — DAEMON as the whole page at daemon.skenmy.com.
//                     No frame, no WM, no achievements. `setArenaScale`
//                     publishes the scale so the page can draw its walls
//                     closing in; `say` publishes the line so the page
//                     can draw it itself.
//
// Deliberate constraints, both load-bearing:
//
//  1. **No runtime imports outside `src/lib`.** `daemon.skenmy.com` must
//     not pull a byte of skenOS. The skenOS-only escape hatches (VAPOUR,
//     the WM) are reached through `window` globals and a late binding,
//     not through `import`, so this module stays tree-shakeable down to
//     the standalone half. The only imports here are type-only, and
//     types are erased.
//
//  2. **Different storage key prefixes.** A personal best set on
//     daemon.skenmy.com must not overwrite one set inside skenOS, and
//     clearing site data on one must not wipe the other. They are
//     different origins today, but skenOS also reads these keys from a
//     future same-origin `/daemon` mount, so the namespaces are kept
//     distinct at the key level rather than relying on the origin.

import type { DaemonHost } from "./host";
import type { WMApi } from "../types";

// ── Storage namespaces ────────────────────────────────────────────────

/** In-OS keys. Sits alongside the rest of skenOS's `skenos.*` prefix. */
export const SKENOS_STORAGE_PREFIX = "skenos.daemon.";

/** Standalone keys. Intentionally *not* under `skenos.` — see note 2. */
export const STANDALONE_STORAGE_PREFIX = "daemon.standalone.";

// ── Reduced motion ────────────────────────────────────────────────────

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/** Read the media query fresh each call. Visitors do flip this mid-run
 *  (macOS "Reduce motion" is a single toggle away) and a cached read
 *  would strand them in whichever mode the page booted in. Guarded for
 *  jsdom and any environment without `matchMedia`. */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try {
    return window.matchMedia(REDUCED_MOTION_QUERY).matches;
  } catch {
    return false;
  }
}

// ── A tiny publish/subscribe, shared by both hosts ────────────────────
//
// The standalone page needs to hear about arena scale and commentary,
// and so does anything in skenOS that wants to mirror them. Rather than
// give the game a second callback channel, both hosts publish and
// whoever cares subscribes.

export const ARENA_SCALE_EVENT = "daemon:arena-scale";
export const SAY_EVENT = "daemon:say";

export type SayTone = "normal" | "boss";
export type SayLine = { line: string; tone: SayTone; at: number };

type Listener<T> = (value: T) => void;

function emitter<T>(): { emit: (v: T) => void; on: (fn: Listener<T>) => () => void } {
  const listeners = new Set<Listener<T>>();
  return {
    emit(v) {
      // Copy before iterating: a listener that unsubscribes itself
      // mid-notify would otherwise mutate the set we're walking.
      for (const fn of Array.from(listeners)) fn(v);
    },
    on(fn) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
  };
}

const arenaScaleBus = emitter<number>();
const sayBus = emitter<SayLine>();

let currentArenaScale = 1;

/** Last scale published by either host. 1 = full size. */
export function getArenaScale(): number {
  return currentArenaScale;
}

/** Subscribe to arena-scale changes. Returns an unsubscribe. */
export function onArenaScale(fn: Listener<number>): () => void {
  return arenaScaleBus.on(fn);
}

/** Subscribe to DAEMON's commentary. Returns an unsubscribe. */
export function onSay(fn: Listener<SayLine>): () => void {
  return sayBus.on(fn);
}

function dispatch(name: string, detail: unknown): void {
  if (typeof window === "undefined" || typeof window.CustomEvent !== "function") return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

function publishArenaScale(scale: number): void {
  const clamped = clamp01(scale);
  currentArenaScale = clamped;
  // Also expose it as a CSS custom property so a wall can be drawn with
  // no JS wiring at all — the standalone page leans on this.
  if (typeof document !== "undefined" && document.documentElement) {
    document.documentElement.style.setProperty("--daemon-arena-scale", String(clamped));
  }
  arenaScaleBus.emit(clamped);
  dispatch(ARENA_SCALE_EVENT, { scale: clamped });
}

function publishSay(line: string, tone: SayTone): void {
  const payload: SayLine = { line, tone, at: Date.now() };
  sayBus.emit(payload);
  dispatch(SAY_EVENT, payload);
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 1;
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

// ── skenOS host ───────────────────────────────────────────────────────

/** What the in-OS DAEMON app hands us so we can drive the real window.
 *  Supplied at mount time by the app; see `attachSkenosHost`. */
export type SkenosBinding = {
  wm: WMApi;
  /** The `Win.id` of the DAEMON window — the frame we shrink. */
  winId: string;
};

/** WindowFrame refuses to resize below these (see `startResize`). Going
 *  under them would desync the drawn arena from the frame, so the shrink
 *  device stops here and the renderer letterboxes the remainder. */
const MIN_W = 280;
const MIN_H = 180;

/** How long a shrink takes when motion is allowed. Long enough to read
 *  as the room closing in, short enough not to eat a spell card. */
const SHRINK_MS = 520;

let binding: SkenosBinding | null = null;

/** Natural (pre-shrink) bounds, captured the first time we shrink so
 *  scale 1 can restore exactly. Cleared on detach. */
let naturalBounds: { x: number; y: number; w: number; h: number } | null = null;
let shrinkRaf = 0;

/**
 * Wire `skenosHost` to a live WM + window. Call on mount of the in-OS
 * DAEMON app; call the returned function on unmount.
 *
 * The host object itself is a module singleton so it can be imported at
 * the top level of an app file without threading React context through
 * the sim. Unbound, every skenOS-only method degrades to a no-op rather
 * than throwing — a headless render or a test that forgot to attach
 * should be boring, not fatal.
 */
export function attachSkenosHost(next: SkenosBinding): () => void {
  binding = next;
  return () => {
    if (binding === next) {
      cancelShrink();
      binding = null;
      naturalBounds = null;
    }
  };
}

/** Current binding, for tests and for the app to assert on. */
export function skenosBinding(): SkenosBinding | null {
  return binding;
}

function cancelShrink(): void {
  if (shrinkRaf && typeof cancelAnimationFrame === "function") cancelAnimationFrame(shrinkRaf);
  shrinkRaf = 0;
}

function applyScale(b: SkenosBinding, natural: { x: number; y: number; w: number; h: number }, scale: number): void {
  // Shrink about the centre. Anchoring to the top-left would slide the
  // playfield out from under the cursor, which reads as a cheap hit.
  const cx = natural.x + natural.w / 2;
  const cy = natural.y + natural.h / 2;
  const w = Math.max(MIN_W, Math.round(natural.w * scale));
  const h = Math.max(MIN_H, Math.round(natural.h * scale));
  b.wm.setBounds(b.winId, {
    x: Math.round(cx - w / 2),
    // 32 clears the menubar — same floor WindowFrame's own drag uses.
    y: Math.max(32, Math.round(cy - h / 2)),
    w,
    h,
  });
}

/** The in-OS host. Bind it with `attachSkenosHost` before use. */
export const skenosHost: DaemonHost = {
  kind: "skenos",

  setArenaScale(scale) {
    const target = clamp01(scale);
    publishArenaScale(target);

    const b = binding;
    if (!b) return;

    const win = b.wm.wins.find((w) => w.id === b.winId);
    if (!win) return;

    // First shrink of the run defines "natural". Snapshot it before we
    // touch anything so scale 1 is a byte-exact restore.
    if (!naturalBounds) naturalBounds = { x: win.x, y: win.y, w: win.w, h: win.h };
    const natural = naturalBounds;

    cancelShrink();

    // Reduced motion: jump. Never animate a frame at someone who asked
    // us not to. The device still works — it just arrives instantly.
    if (prefersReducedMotion() || typeof requestAnimationFrame !== "function") {
      applyScale(b, natural, target);
      if (target >= 1) naturalBounds = null;
      return;
    }

    const from = natural.w > 0 ? win.w / natural.w : 1;
    const started = typeof performance !== "undefined" ? performance.now() : Date.now();

    const frame = (): void => {
      const now = typeof performance !== "undefined" ? performance.now() : Date.now();
      const t = Math.min(1, (now - started) / SHRINK_MS);
      // easeOutCubic — fast commitment, soft landing.
      const eased = 1 - Math.pow(1 - t, 3);
      applyScale(b, natural, from + (target - from) * eased);
      if (t < 1) {
        shrinkRaf = requestAnimationFrame(frame);
      } else {
        shrinkRaf = 0;
        if (target >= 1) naturalBounds = null;
      }
    };
    shrinkRaf = requestAnimationFrame(frame);
  },

  say(line, tone = "normal") {
    publishSay(line, tone);
    // "boss" is DAEMON in its escalated register — warn tint, and it
    // lingers, because those lines carry mechanics.
    binding?.wm.notify(line, tone === "boss" ? "warn" : "info", tone === "boss" ? 6000 : 3500);
  },

  unlock(id) {
    // Reached through the global rather than `import { VAPOUR }` so the
    // standalone bundle never sees `src/apps/vapour`. vapour.tsx
    // installs this on mount; before then, unlocks are dropped, which
    // is the correct behaviour for an achievement system that isn't up.
    const w = globalThis as unknown as { VAPOUR_UNLOCK?: (id: string) => void };
    w.VAPOUR_UNLOCK?.(id);
  },

  storageKey(suffix) {
    return SKENOS_STORAGE_PREFIX + suffix;
  },

  reducedMotion: prefersReducedMotion,
};

// ── Standalone host ───────────────────────────────────────────────────

/**
 * Build a host for daemon.skenmy.com.
 *
 * Everything skenOS-shaped is either replaced (arena scale becomes a
 * published number the page draws walls from; `say` becomes a published
 * line the page draws itself) or dropped (`unlock` — there is no VAPOUR
 * here and the game must never read the return).
 */
export function createStandaloneHost(): DaemonHost {
  return {
    kind: "standalone",

    // No window to shrink. Publish the scale and let the page close its
    // own walls in — same numbers, different expression.
    setArenaScale: publishArenaScale,

    say: (line, tone = "normal") => publishSay(line, tone),

    // Inert by design. Not a throw, not a warning, not a queue that
    // grows forever — the standalone build has no achievement system
    // and pretending otherwise would leak skenOS concepts into a page
    // that deliberately has none.
    unlock: () => {},

    storageKey: (suffix) => STANDALONE_STORAGE_PREFIX + suffix,

    reducedMotion: prefersReducedMotion,
  };
}

/** The standalone host. Stateless, so a singleton is fine. */
export const standaloneHost: DaemonHost = createStandaloneHost();

/** Reset module state between tests. Not used by the app. */
export function __resetHostsForTest(): void {
  cancelShrink();
  binding = null;
  naturalBounds = null;
  currentArenaScale = 1;
}
